// terrainSampler.ts — Client-side terrain elevation lookup.
//
// Fetches AWS Terrarium DEM tiles via our /api/wx/terrain proxy, decodes
// pixel-encoded elevation, caches tile ImageData, and exposes a
// `sample(lat, lon)` API that returns elevation in METERS.
//
// Designed for bulk usage (thousands of samples per glide-ring update):
//   - Tile bitmaps cached forever (DEM data never changes).
//   - ImageData cached forever per tile.
//   - In-flight fetches deduplicated.
//   - Bilinear interpolation between the 4 surrounding tile pixels for
//     smooth gradients (vs nearest-neighbor which produces stairsteps).
//
// Encoding (Terrarium):
//   elevation_meters = (R * 256 + G + B / 256) - 32768
//
// Reference docs: data/plate-overlay-research-2026-05-14.md ✕ — this is
// not plate code; just neighbor. See AWS Terrain Tiles registry.

const FT_PER_M = 3.28084;
const DEM_TILE_PX = 256;

/**
 * Default zoom for general terrain sampling. z=11 gives ~75–150 m / px
 * at mid-latitudes, which is fine for AGL / glide-ring use. Higher
 * zoom = more tiles to fetch; lower zoom = coarser elevations.
 */
export const DEFAULT_DEM_ZOOM = 11;

// ─── Tile math ────────────────────────────────────────────────────────────

function lonToTileX(lon: number, z: number): number {
  return ((lon + 180) / 360) * (1 << z);
}
function latToTileY(lat: number, z: number): number {
  const rad = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * (1 << z);
}

/** Decode one Terrarium pixel → elevation (meters). */
export function terrariumDecode(r: number, g: number, b: number): number {
  return r * 256 + g + b / 256 - 32768;
}

// ─── Tile cache ───────────────────────────────────────────────────────────

// 2026-05-16 perf audit (F2): both caches grew unboundedly. A multi-hour
// flight over CONUS at z=11 retains hundreds of MB of CPU-side ImageData
// (256×256×4 = 256 KB per tile) plus GPU-backed ImageBitmap handles. iPad
// Safari soft-caps the JS heap at ~1.5 GB; once breached the compositor
// throttles and pan jankifies.
//
// ME-19 (audit 2026-05-22): original eviction was FIFO (Map insertion-order)
// even though the comment said "LRU". Tiles that are frequently accessed by
// the glide-ring sampler (the tile right below the aircraft) were getting
// evicted ahead of tiles fetched once and never touched again, because FIFO
// only considers insertion time, not recency of access.
//
// Now uses true LRU: getTileImageData promotes a cache-hit entry to the back
// of the Map (delete + re-insert) so eviction always removes the
// least-recently-ACCESSED tile first, not the least-recently-INSERTED one.
const MAX_TERRAIN_TILES = 512;  // ≈ 128 MB ceiling on imageDataCache
const bitmapCache = new Map<string, Promise<ImageBitmap | null>>();
const imageDataCache = new Map<string, ImageData>();

/**
 * Evict the least-recently-accessed tiles when the cache exceeds MAX_TERRAIN_TILES.
 * Because getTileImageData promotes hits to the back (delete + re-insert),
 * `Map` insertion order equals recency-of-access order: the front is the LRU entry.
 * Also closes the underlying ImageBitmap promise to release any held GPU memory.
 */
function evictTerrainIfFull(): void {
  while (imageDataCache.size > MAX_TERRAIN_TILES) {
    const lru = imageDataCache.keys().next().value;
    if (lru === undefined) break;
    imageDataCache.delete(lru);
    const bm = bitmapCache.get(lru);
    bitmapCache.delete(lru);
    bm?.then((b) => { try { b?.close?.(); } catch { /* ignore */ } }).catch(() => {});
  }
}

function tileKey(z: number, x: number, y: number): string {
  return `${z}/${x}/${y}`;
}

async function fetchTileBitmap(
  z: number,
  x: number,
  y: number,
  signal?: AbortSignal,
): Promise<ImageBitmap | null> {
  try {
    const res = await fetch(`/api/wx/terrain?z=${z}&x=${x}&y=${y}`, { signal });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await createImageBitmap(blob);
  } catch {
    return null;
  }
}

async function getTileImageData(
  z: number,
  x: number,
  y: number,
  signal?: AbortSignal,
): Promise<ImageData | null> {
  const key = tileKey(z, x, y);
  const cached = imageDataCache.get(key);
  if (cached) {
    // LRU promotion: move this entry to the back so it's evicted last.
    imageDataCache.delete(key);
    imageDataCache.set(key, cached);
    return cached;
  }

  let bitmapP = bitmapCache.get(key);
  if (!bitmapP) {
    bitmapP = fetchTileBitmap(z, x, y, signal);
    bitmapCache.set(key, bitmapP);
  }
  const bitmap = await bitmapP;
  if (!bitmap) return null;

  // Draw into an OffscreenCanvas (or DOM canvas fallback) and read pixels.
  type Canvas2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;
  let ctx: Canvas2D | null = null;
  if (typeof OffscreenCanvas !== 'undefined') {
    const c = new OffscreenCanvas(DEM_TILE_PX, DEM_TILE_PX);
    ctx = c.getContext('2d') as OffscreenCanvasRenderingContext2D | null;
  }
  if (!ctx) {
    const c = document.createElement('canvas');
    c.width = DEM_TILE_PX;
    c.height = DEM_TILE_PX;
    ctx = c.getContext('2d') as CanvasRenderingContext2D | null;
  }
  if (!ctx) return null;
  ctx.drawImage(bitmap as unknown as CanvasImageSource, 0, 0);
  const img = ctx.getImageData(0, 0, DEM_TILE_PX, DEM_TILE_PX);
  imageDataCache.set(key, img);
  // F2: release GPU-backed bitmap now that CPU-side pixels are extracted.
  // Keeping it pinned doubles GPU memory per cached tile.
  try { bitmap.close?.(); } catch { /* ignore */ }
  evictTerrainIfFull();
  return img;
}

function pixelElevM(img: ImageData, px: number, py: number): number {
  const i = (py * DEM_TILE_PX + px) * 4;
  return terrariumDecode(img.data[i], img.data[i + 1], img.data[i + 2]);
}

// ─── Public API ───────────────────────────────────────────────────────────

/**
 * Sample terrain elevation at (lat, lon) in METERS. Returns `NaN` when
 * the underlying tile fails to load. Bilinearly interpolated between
 * the four neighboring tile pixels.
 */
export async function sampleElevationM(
  lat: number,
  lon: number,
  zoom: number = DEFAULT_DEM_ZOOM,
  signal?: AbortSignal,
): Promise<number> {
  const xf = lonToTileX(lon, zoom);
  const yf = latToTileY(lat, zoom);
  const xi = Math.floor(xf);
  const yi = Math.floor(yf);

  // Position inside the tile (0..1).
  const fx = xf - xi;
  const fy = yf - yi;

  // Pixel coordinates (continuous) inside the tile.
  const pxF = fx * DEM_TILE_PX;
  const pyF = fy * DEM_TILE_PX;

  const px0 = Math.floor(pxF);
  const py0 = Math.floor(pyF);
  const dx = pxF - px0;
  const dy = pyF - py0;

  const img = await getTileImageData(zoom, xi, yi, signal);
  if (!img) return NaN;

  // Bilinear interpolation between the 4 neighbors. We clamp neighbor
  // coords to stay inside the tile (cross-tile bilinear would require
  // fetching up to 3 adjacent tiles; not worth the latency at z=11).
  const x1 = Math.min(DEM_TILE_PX - 1, px0 + 1);
  const y1 = Math.min(DEM_TILE_PX - 1, py0 + 1);
  const x0 = Math.min(DEM_TILE_PX - 1, Math.max(0, px0));
  const y0 = Math.min(DEM_TILE_PX - 1, Math.max(0, py0));

  const e00 = pixelElevM(img, x0, y0);
  const e10 = pixelElevM(img, x1, y0);
  const e01 = pixelElevM(img, x0, y1);
  const e11 = pixelElevM(img, x1, y1);
  return e00 * (1 - dx) * (1 - dy) + e10 * dx * (1 - dy) + e01 * (1 - dx) * dy + e11 * dx * dy;
}

/** Convenience: sample in feet instead of meters. */
export async function sampleElevationFt(
  lat: number,
  lon: number,
  zoom: number = DEFAULT_DEM_ZOOM,
  signal?: AbortSignal,
): Promise<number> {
  const m = await sampleElevationM(lat, lon, zoom, signal);
  return Number.isFinite(m) ? m * FT_PER_M : NaN;
}

/**
 * Preload terrain tiles covering a bounding box at the given zoom.
 * Useful before bulk sampling (e.g. glide-ring polygon trace) to make
 * subsequent `sampleElevation` calls instant.
 */
export async function preloadTerrainBbox(
  minLat: number,
  minLon: number,
  maxLat: number,
  maxLon: number,
  zoom: number = DEFAULT_DEM_ZOOM,
  signal?: AbortSignal,
): Promise<void> {
  const x0 = Math.floor(lonToTileX(minLon, zoom));
  const x1 = Math.floor(lonToTileX(maxLon, zoom));
  // Note: latToTileY is inverted (north has smaller y).
  const y0 = Math.floor(latToTileY(maxLat, zoom));
  const y1 = Math.floor(latToTileY(minLat, zoom));
  const tasks: Array<Promise<unknown>> = [];
  for (let x = x0; x <= x1; x++) {
    for (let y = y0; y <= y1; y++) {
      tasks.push(getTileImageData(zoom, x, y, signal));
    }
  }
  await Promise.all(tasks);
}

/**
 * For tests / debugging: clear all in-memory tile caches. Not exported
 * from the public surface; kept here for unit-test use.
 */
export function _resetTerrainCache(): void {
  bitmapCache.clear();
  imageDataCache.clear();
}

// ─── Bulk elevation grid (for off-main-thread routing) ────────────────────

export interface ElevationGridBuffer {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
  cols: number;
  rows: number;
  /** Elevation samples (feet MSL), row-major. */
  data: Float32Array;
}

/**
 * Build a packed elevation grid (Float32Array of feet MSL) covering
 * the requested bbox by reading directly from the in-memory tile
 * cache. The bbox is divided into `cols * rows` cells; each cell's
 * elevation is sampled at its center via bilinear interpolation.
 *
 * `preloadTerrainBbox()` MUST be awaited for the same bbox+zoom
 * before calling this — otherwise tiles will be missing and the
 * corresponding cells will be filled with NaN.
 *
 * Designed to be transferred to a Web Worker (the `data` Float32Array
 * is a transferable). After transfer, the main-thread buffer is
 * detached; clone it first if you need to keep using it.
 */
export async function buildElevationGrid(
  minLat: number,
  minLon: number,
  maxLat: number,
  maxLon: number,
  zoom: number = DEFAULT_DEM_ZOOM,
  approxStepNm = 0.2,
  signal?: AbortSignal,
): Promise<ElevationGridBuffer> {
  // Cell size: convert NM to degrees. 1 NM lat = 1/60 deg ≈ 0.01667 deg.
  // 1 NM lon ≈ 0.01667 / cos(lat) at the midpoint of the bbox.
  const midLat = (minLat + maxLat) / 2;
  const degLatPerNm = 1 / 60;
  const degLonPerNm = 1 / 60 / Math.max(0.01, Math.cos((midLat * Math.PI) / 180));
  const dLat = approxStepNm * degLatPerNm;
  const dLon = approxStepNm * degLonPerNm;
  const rows = Math.max(2, Math.ceil((maxLat - minLat) / dLat) + 1);
  const cols = Math.max(2, Math.ceil((maxLon - minLon) / dLon) + 1);
  const data = new Float32Array(rows * cols);
  // Sample each cell at its center.
  for (let y = 0; y < rows; y++) {
    const lat = minLat + (y / (rows - 1)) * (maxLat - minLat);
    for (let x = 0; x < cols; x++) {
      const lon = minLon + (x / (cols - 1)) * (maxLon - minLon);
      // sampleElevationFt returns a Promise; we rely on tiles already
      // being warm in the cache (so this resolves synchronously off
      // the cache map). If a tile is missing, the await will fall
      // back to fetching it now.
      // eslint-disable-next-line no-await-in-loop
      const ft = await sampleElevationFt(lat, lon, zoom, signal);
      data[y * cols + x] = Number.isFinite(ft) ? ft : NaN;
    }
  }
  return { minLat, maxLat, minLon, maxLon, cols, rows, data };
}
